import React from 'react';

import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import ChooseDataFrameCreationMethodView from 'analysis-tool/data-frame-creation/ChooseDataFrameCreationMethodView';
import CombineDataFramesView from 'analysis-tool/data-frame-creation/combine-data-frames/CombineDataFramesView';
import CreateDataFrameFromConnectionDataView from 'analysis-tool/data-frame-creation/connections/CreateDataFrameFromConnectionDataView';
import CreateDataFrameFromAnalyticsBlockView from 'analysis-tool/data-frame-creation/CreateDataFrameFromAnalyticsBlockView';
import CreateDataFrameFromDataCatalogView from 'analysis-tool/data-frame-creation/data-catalog/CreateDataFrameFromDataCatalogView';
import CreateDataFrameFromLookUpView from 'analysis-tool/data-frame-creation/look-up/LookUpView';
import PlateSchemaDataFrameCreationView from 'analysis-tool/data-frame-creation/schemas/PlateSchemaDataFrameCreationView';
import RegistrySchemasDataFrameCreationView from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasDataFrameCreationView';
import CreateDataFrameFromUploadedCSVView from 'analysis-tool/data-frame-creation/upload-csv/CreateDataFrameFromUploadedCSVView';
import AnalysisDataLayoutAdapter from 'analysis-tool/data-layout/AnalysisDataLayoutAdapter';
import AnalysisDataViewAdapter from 'analysis-tool/data-view/AnalysisDataViewAdapter';
import AnalysisCreateOutputsView from 'analysis-tool/outputs/AnalysisCreateOutputsView';
import ExportDataCatalogDatasetOutputView from 'analysis-tool/outputs/ExportDataCatalogDatasetOutputView';
import SendToDocumentView from 'analysis-tool/outputs/SendToDocumentView';
import SaveToResultsStepConfigurationView from 'analysis-tool/structured-data-mapping/SaveToResultsStepConfigurationView';
import SaveToResultsStepOutputView from 'analysis-tool/structured-data-mapping/SaveToResultsStepOutputView';
import SaveToResultsStepReconfigurationView from 'analysis-tool/structured-data-mapping/SaveToResultsStepReconfigurationView';
import AnalysisTemplateApplyView from 'analysis-tool/templates/AnalysisTemplateApplyView';
import AnalysisTemplateConfigurationView from 'analysis-tool/templates/AnalysisTemplateConfigurationView';
import AnalysisComponentErrorBoundary from 'analysis-tool/ui/components/AnalysisComponentErrorBoundary';
import AnalysisFlowchartView from 'analysis-tool/ui/flowchart-view/AnalysisFlowchartView';
import useLogModalSupportedStepProfilerTimings from 'analysis-tool/ui/hooks/useLogModalSupportedStepProfilerTimings';
import ExploratoryDataAnalysisDashboardView from 'machine-learning/parameter-optimization/ExploratoryDataAnalysisDashboardView';
import ParameterOptimizationDatasetConfigurationDashboardView from 'machine-learning/parameter-optimization/legacy-components/ParameterOptimizationDatasetConfigurationDashboardView';
import ReadOnlyParameterOptimizationDatasetView from 'machine-learning/parameter-optimization/legacy-components/ReadOnlyParameterOptimizationDatasetView';
import ParameterOptimizationModelingDashboardView from 'machine-learning/parameter-optimization/ParameterOptimizationModelingDashboardView';
import ParameterOptimizationModelingOverviewView from 'machine-learning/parameter-optimization/ParameterOptimizationModelingOverviewView';
import ParameterOptimizationPredictionStepView from 'machine-learning/parameter-optimization/prediction/ParameterOptimizationPredictionStepView';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import ChartView from './ChartView';
import DataFrameView from './DataFrameView';
import EditChartView from './EditChartView';
import EditRegressionView from './EditRegressionView';
import EmptyAnalysisView from './EmptyAnalysisView';
import FailedStepView from './FailedStepView';
import FileDashboardView from './files/FileDashboardView';
import FileView from './files/FileView';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  MainViewComponentProps,
  StepAwareAnalysisMainViewItemType,
} from './MainViewTypes';
import RegressionView from './RegressionView';
import RunningStepView from './RunningStepView';
import ThirdPartyStepView from './third-party-step/ThirdPartyStepView';

const ITEM_TYPE_TO_VIEW_COMPONENT: {
  [key in AnalysisMainViewItemType]: React.FC<MainViewComponentProps<key>>;
} = {
  [AnalysisMainViewItemType.CHART]: ChartView,
  [AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD]: ChooseDataFrameCreationMethodView,
  [AnalysisMainViewItemType.COMBINE_DATA_FRAMES]: CombineDataFramesView,
  [AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION]: CreateDataFrameFromConnectionDataView,
  [AnalysisMainViewItemType.CREATE_OUTPUTS]: AnalysisCreateOutputsView,
  [AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION]: CreateDataFrameFromDataCatalogView,
  [AnalysisMainViewItemType.DATA_LAYOUT]: AnalysisDataLayoutAdapter,
  [AnalysisMainViewItemType.DATA_VIEW]: AnalysisDataViewAdapter,
  [AnalysisMainViewItemType.EDIT_CHART]: EditChartView,
  [AnalysisMainViewItemType.EMPTY_ANALYSIS]: EmptyAnalysisView,
  [AnalysisMainViewItemType.EXPLORATORY_DATA_ANALYSIS]: ExploratoryDataAnalysisDashboardView,
  [AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT]: ExportDataCatalogDatasetOutputView,
  [AnalysisMainViewItemType.DATA_FRAME]: DataFrameView,
  [AnalysisMainViewItemType.FAILED]: FailedStepView,
  [AnalysisMainViewItemType.FILE]: FileView,
  [AnalysisMainViewItemType.FILE_DASHBOARD]: FileDashboardView,
  [AnalysisMainViewItemType.FLOWCHART]: AnalysisFlowchartView,
  [AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION]: CreateDataFrameFromAnalyticsBlockView,
  [AnalysisMainViewItemType.LOOK_UP]: CreateDataFrameFromLookUpView,
  [AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION_DASHBOARD]:
    ParameterOptimizationDatasetConfigurationDashboardView,
  [AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD]:
    ParameterOptimizationModelingDashboardView,
  [AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW]:
    ParameterOptimizationModelingOverviewView,
  [AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_PREDICTION]: ParameterOptimizationPredictionStepView,
  [AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION]: RegistrySchemasDataFrameCreationView,
  [AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION]: PlateSchemaDataFrameCreationView,
  [AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION]: CreateDataFrameFromUploadedCSVView,
  [AnalysisMainViewItemType.RUNNING]: RunningStepView,
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION]: SaveToResultsStepConfigurationView,
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT]: SaveToResultsStepOutputView,
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION]: SaveToResultsStepReconfigurationView,
  [AnalysisMainViewItemType.SEND_TO_DOCUMENT]: SendToDocumentView,
  [AnalysisMainViewItemType.TEMPLATE_APPLY]: AnalysisTemplateApplyView,
  [AnalysisMainViewItemType.TEMPLATE_CONFIGURATION]: AnalysisTemplateConfigurationView,
  [AnalysisMainViewItemType.THIRD_PARTY_STEP]: ThirdPartyStepView,
  [AnalysisMainViewItemType.VIEW_PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION]:
    ReadOnlyParameterOptimizationDatasetView,
  [AnalysisMainViewItemType.REGRESSION]: RegressionView,
  [AnalysisMainViewItemType.EDIT_REGRESSION]: EditRegressionView,
};

// This function is expected to return a clientStateId that will be unique for a given instance of a MainView
// component within a single Analysis; hence e.g. appending the `stepId` for step-aware components (which,
// in the future, may have multiple instances within an Analysis)
function getClientStateIdForMainView(
  clientStateIdForAnalysis: string,
  selectedItem: AnalysisMainViewItem
): string {
  if (isStepAwareAnalysisItem(selectedItem)) {
    return `${clientStateIdForAnalysis}-${selectedItem.stepId}`;
  } else {
    return clientStateIdForAnalysis;
  }
}

// The AnalysisUIMainViewRouter handles mapping from (selectedItem + analysis) -> step when necessary.
export type AnalysisUIMainViewRouterProps = Omit<
  MainViewComponentProps<AnalysisMainViewItemType>,
  'step' | 'writable'
>;

/** Router for mapping (analysis + selectedItem + shared props) to a component to render in the AnalysisUI
 *  main view.
 *
 * The router allows for two different types of "main view" components:
 * - Step-agnostic views. These are views that don't have a related Pipeline step. For example,
 *   EmptyAnalysisView does not have a related step and therefore should not be passed a step prop.
 * - Step-aware views. These are views that do have a related Pipeline step. For example, ThirdPartyStepView
 *   needs to be aware of its related THIRD_PARTY pipeline step, and so is passed the relevant GraphQL step
 *   object.
 *
 * The choice to use steps as an important piece of data is intentional for a few reasons:
 * - It improves testability and composability to pass only as much information as is needed. We pass in the
 *   analysis to accommodate instances in which a step needs to other steps in the analysis.
 * - This works towards having sidebar items, header breadcrumbs, and main view items all being derived from
 *   the selected AnalysisMainViewItem.
 * - We want to have consistency in the props passed to all main views (like a Plugin system), rather than
 *   handling each possible type -> component + props mapping explicitly and separately. This has advantages
 *   (e.g. making it easier to add new main views) and disadvantages (e.g. main views receive more props
 *   than they necessarily need).
 */
export default function AnalysisUIMainViewRouter({
  analysis,
  clientStateId,
  selectedItem,
  ...restProps
}: AnalysisUIMainViewRouterProps): JSX.Element {
  const mainViewClientStateId = getClientStateIdForMainView(clientStateId, selectedItem);
  useLogModalSupportedStepProfilerTimings(analysis);

  // eslint-disable-next-line benchlint/no-in-operator
  if ('isUpdating' in selectedItem && selectedItem.isUpdating) {
    return <CenteredHorizontalThrobber />;
  }

  if (isStepAwareAnalysisItem(selectedItem)) {
    const ViewComponent: React.FC<MainViewComponentProps<StepAwareAnalysisMainViewItemType>> =
      ITEM_TYPE_TO_VIEW_COMPONENT[selectedItem.type];
    const step = getLegacyStepFromAnalysis(analysis, selectedItem.stepId);
    return (
      <AnalysisComponentErrorBoundary
        errorExtras={{
          analysisId: analysis.id,
          selectedItem,
          stepId: step.id,
          stepType: step.definition.stepType,
        }}
      >
        <ViewComponent
          {...restProps}
          analysis={analysis}
          clientStateId={mainViewClientStateId}
          selectedItem={selectedItem}
          step={step}
        />
      </AnalysisComponentErrorBoundary>
    );
  } else {
    const ViewComponent: React.FC<
      MainViewComponentProps<Exclude<AnalysisMainViewItemType, StepAwareAnalysisMainViewItemType>>
    > = ITEM_TYPE_TO_VIEW_COMPONENT[selectedItem.type];
    return (
      <AnalysisComponentErrorBoundary errorExtras={{analysisId: analysis.id, selectedItem}}>
        <ViewComponent
          {...restProps}
          analysis={analysis}
          clientStateId={mainViewClientStateId}
          selectedItem={selectedItem}
        />
      </AnalysisComponentErrorBoundary>
    );
  }
}
